import { ChangeDetectionStrategy, Component, OnDestroy, inject } from '@angular/core';
import {Subject, takeUntil} from 'rxjs';
import {HistoryStore} from 'src/app/modules/core/store';
import * as _ from 'lodash';
import moment from 'moment';

import {
    BlockActivityInfoUpdatedBy,
    PassedPosition,
    VehicleHistory
} from 'src/app/modules/shared/models/vehicle-history';
import { MatSliderModule } from '@angular/material/slider';


@Component({
    selector: 'app-history-scrubbar',
    templateUrl: './historyScrubbar.component.html',
    styleUrls: ['./historyScrubbar.component.scss'],
    imports: [MatSliderModule],
    changeDetection: ChangeDetectionStrategy.Eager
})
export class HistoryScrubbarComponent implements OnDestroy {
    private _historyStore = inject(HistoryStore);

    public min = 0;
    public max = 1;
    public step = 1;
    public value = 0;
    public tickInterval = 1;
    public passedPositions: PassedPosition[];
    public left;

    public firstValue: string;
    public lastValue: string;
    public isPlaying = false;

    private _ngUnsubscribe$ = new Subject<void>();
    private _intervalTimer;

    public get isRestart(): boolean {
        return this.value === this.passedPositions.length - 1;
    }


    constructor() {
        this._historyStore.getPropertyState('vehicleHistory')
            .pipe(
                takeUntil(this._ngUnsubscribe$)
            ).subscribe((vehicleHistory: VehicleHistory) => {
                this.passedPositions = vehicleHistory ? vehicleHistory.passedPositions : undefined;

                if (this.passedPositions) {
                    this.max = this.passedPositions.length - 1;

                    this.firstValue = moment(_.first(this.passedPositions).ts).format('HH:mm');
                    this.lastValue = moment(_.last(this.passedPositions).ts).format('HH:mm');

                    this._highlightTimeByScrubbarIndex(0);
                } else {
                    this.max = 1;
                    this.firstValue = undefined;
                    this.lastValue = undefined;
                }
            });

        this._historyStore.getPropertyState('highlightedTime')
            .pipe(
                takeUntil(this._ngUnsubscribe$)
            ).subscribe((highlightedTime: PassedPosition) => {
                if (this.passedPositions) {
                    this.value = _.indexOf(this.passedPositions, highlightedTime);
                }
            });

    }

    public ngOnDestroy() {
        this._ngUnsubscribe$.next();
        this._ngUnsubscribe$.complete();
        clearInterval(this._intervalTimer);
    }

    public valueUpdated(value: number) {
        this._highlightTimeByScrubbarIndex(value, true, BlockActivityInfoUpdatedBy.scrubbarTimelineClick);
    }

    public togglePlay() {
        if (this.isRestart) {
            this._restart();
        } else if (!this.isPlaying) {
            this._start();
        } else {
            this._pause();
        }
    }

    formatLabel = (value: number): string => {
        if (this.passedPositions && this.passedPositions.length > 0 && this.passedPositions[this.value]) {
            return moment(this.passedPositions[this.value].ts).format('HH:mm:ss');
        }
    };

    private _highlightTimeByScrubbarIndex(scrubbarIndex: number, isClicked = false, updatedBy = BlockActivityInfoUpdatedBy.else) {
        if (!(this.passedPositions && this.passedPositions[scrubbarIndex])) {
            return;
        }

        const passedPosition = this.passedPositions[scrubbarIndex];
        this._historyStore.selectActivityBasedOnPosition(passedPosition, updatedBy);
        this._historyStore.updateHighlightedTime(passedPosition, isClicked);
    }

    private _start() {
        this.isPlaying = true;
        clearInterval(this._intervalTimer);
        this._intervalTimer = setInterval(() => {
            if (this.value < this.passedPositions?.length) {
                const nextIndex = this.value + 1;
                this._highlightTimeByScrubbarIndex(nextIndex, false);
            } else {
                this._pause();
            }
        }, 100);
    }

    private _restart() {
        // start from the beginning
        this._highlightTimeByScrubbarIndex(0);
        this._start();
    }

    private _pause() {
        this.isPlaying = false;

        clearInterval(this._intervalTimer);
    }

}
